import { Link } from 'react-router';
import React, { useCallback, useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { trackTileMediaDetailClick } from '~/modules/tracking/util/trackTileMedia';
import { useTileAdminActions } from '@modules-rocco/admin/hooks/use-tile-admin-actions';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import { AdminTile } from '@rocco/ui/tile-shared/components/AdminTile';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';
import { useRootPermissions } from '~/modules/user';
import { useLibraryAction } from '~/modules/directory/context/LibraryActionContext';

interface DirectoryProductTileProps {
    tile: TileViewModel<ProductTileViewModel>;
    isPending?: boolean;
    isFollowed?: boolean;
}

// Selector: editProfile is mount-stable, so the picked branch never swaps
// during a session and the chosen tile's <img> is never remounted.
export const DirectoryProductTile = React.memo(
    (props: DirectoryProductTileProps) => {
        const { editProfile } = useRootPermissions();
        return editProfile ? (
            <AdminDirectoryProductTile {...props} />
        ) : (
            <RegularDirectoryProductTile {...props} />
        );
    },
);

DirectoryProductTile.displayName = 'DirectoryProductTile';

const RegularDirectoryProductTile = ({
    tile,
    isPending = false,
}: DirectoryProductTileProps) => {
    const tileRef = useRef<HTMLDivElement>(null);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tracker = useTracker();
    const { model } = tile;
    const primaryImage = model.images?.[0];
    const tileActions = useAtomValue(uiContextAtomTileActions);

    const handleMouseEnter = useCallback(() => {
        hoverTimerRef.current = setTimeout(() => {
            tileActions?.trackingConfig?.onHover?.(
                tileRef,
                model.id,
                model.professional?.id,
            );
        }, 500);
    }, [tileActions, model.id, model.professional?.id]);

    const handleMouseLeave = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
            }
        };
    }, []);

    const handleClick = () => {
        trackTileMediaDetailClick(tracker, {
            itemID: model.id,
            itemType: 'ProductTile',
            itemName: model.title,
        });
    };

    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: model.id,
            professionalID: model.professional?.id ?? 0,
        },
        ref: tileRef,
    });

    return (
        <div
            ref={tileRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={cn(
                'group relative transition-opacity duration-300',
                isPending && 'opacity-60',
            )}
        >
            <Link
                to={model.link}
                prefetch="intent"
                onClick={handleClick}
                className="flex flex-col gap-[0.9375rem]"
                aria-label={`Product: ${model.title}`}
                data-product-tile
            >
                <div className="aspect-square w-full overflow-hidden rounded-lg">
                    <div className="relative h-full overflow-hidden bg-[#FAFAFA]">
                        {primaryImage && (
                            <Image
                                src={primaryImage.src}
                                alt=""
                                fill
                                fit={
                                    primaryImage.multiplied
                                        ? 'contain'
                                        : 'cover'
                                }
                                multiplied={primaryImage.multiplied}
                                sizes="(min-width: 1921px) 348px, ((min-width: 1024px) and (max-width: 1920px)) 18vw, 49vw"
                                className="transition-transform duration-300 group-hover:scale-105"
                            />
                        )}

                        {!tile.hideFollowButton && (
                            <TileOverlayWithFollowSelector
                                pinId={model.id}
                                pinType="ProductPin"
                            />
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-0.5">
                    <TextTooltip content={model.title}>
                        <p className="line-clamp-1 text-lg font-medium leading-[1.3] tracking-[-0.0225rem] text-[#141414]">
                            {model.title}
                        </p>
                    </TextTooltip>
                    <TextTooltip content={model.professional?.name ?? ''}>
                        <p className="line-clamp-1 text-base font-medium leading-[1.3] tracking-[-0.02rem] text-[#737373]">
                            {model.professional?.name}
                        </p>
                    </TextTooltip>
                </div>
            </Link>
        </div>
    );
};

const AdminDirectoryProductTile = ({
    tile,
    isPending = false,
}: DirectoryProductTileProps) => {
    const tileRef = useRef<HTMLDivElement>(null);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tracker = useTracker();
    const { model } = tile;
    const primaryImage = model.images?.[0];
    const tileActions = useAtomValue(uiContextAtomTileActions);

    const { removeFromLibrary, removedFromLibraryIds } = useLibraryAction();
    const isFadingOut = removedFromLibraryIds?.has(model.id) ?? false;
    const adminActions = useTileAdminActions({
        item: {
            id: model.id.toString(),
            link: model.link ?? '',
            professionalLink: model.professional?.link ?? '',
            pinned: tile.adminStatusInfo?.pinned,
            featuredBrandPinned: tile.adminStatusInfo?.featuredBrandPinned,
            productsAllowed: tile.adminStatusInfo?.productsAllowed,
        },
        itemType: 'products',
        onRemoveFromLibrary:
            removeFromLibrary && !isFadingOut
                ? () =>
                      removeFromLibrary('product', model.id, model.title ?? '')
                : undefined,
    });

    const handleMouseEnter = useCallback(() => {
        hoverTimerRef.current = setTimeout(() => {
            tileActions?.trackingConfig?.onHover?.(
                tileRef,
                model.id,
                model.professional?.id,
            );
        }, 500);
    }, [tileActions, model.id, model.professional?.id]);

    const handleMouseLeave = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
            }
        };
    }, []);

    const handleClick = () => {
        trackTileMediaDetailClick(tracker, {
            itemID: model.id,
            itemType: 'ProductTile',
            itemName: model.title,
        });
    };

    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: model.id,
            professionalID: model.professional?.id ?? 0,
        },
        ref: tileRef,
    });

    return (
        <AdminTile tile={tile} actions={adminActions}>
            <div
                ref={tileRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={cn(
                    'group relative transition-all duration-500 ease-in-out',
                    isPending && 'opacity-60',
                    isFadingOut && 'pointer-events-none scale-95 opacity-0',
                )}
            >
                <Link
                    to={model.link}
                    prefetch="intent"
                    onClick={handleClick}
                    className="gap-3.75 flex flex-col"
                    aria-label={`Product: ${model.title}`}
                    data-product-tile
                >
                    <div className="aspect-square w-full overflow-hidden rounded-lg">
                        <div className="relative h-full overflow-hidden bg-[#FAFAFA]">
                            {primaryImage && (
                                <Image
                                    src={primaryImage.src}
                                    alt=""
                                    fill
                                    fit={
                                        primaryImage.multiplied
                                            ? 'contain'
                                            : 'cover'
                                    }
                                    multiplied={primaryImage.multiplied}
                                    sizes="(min-width: 1921px) 348px, ((min-width: 1024px) and (max-width: 1920px)) 18vw, 49vw"
                                    className="transition-transform duration-300 group-hover:scale-105"
                                />
                            )}

                            {!tile.hideFollowButton && (
                                <TileOverlayWithFollowSelector
                                    pinId={model.id}
                                    pinType="ProductPin"
                                />
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-0.5">
                        <TextTooltip content={model.title}>
                            <p className="line-clamp-1 text-lg font-medium leading-[1.3] tracking-[-0.0225rem] text-[#141414]">
                                {model.title}
                            </p>
                        </TextTooltip>
                        <TextTooltip content={model.professional?.name ?? ''}>
                            <p className="line-clamp-1 text-base font-medium leading-[1.3] tracking-[-0.02rem] text-[#737373]">
                                {model.professional?.name}
                            </p>
                        </TextTooltip>
                    </div>
                </Link>
            </div>
        </AdminTile>
    );
};
